import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { Category } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';

@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './categories.component.html',
  styleUrls: ['../../settings-shared.css', './categories.component.css'],
})
export class CategoriesComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');
  protected readonly toastIsError = signal(false);

  protected categoryForm: Omit<Category, 'id'> = {
    code: '',
    name: '',
    description: '',
    status: 'active',
  };

  protected readonly categories = this.settings.allCategories;

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Category): void {
    this.editingId.set(row.id);
    this.categoryForm = {
      code: row.code,
      name: row.name,
      description: row.description || '',
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.categoryForm.name?.trim()) {
      this.showNotice('Name is required', true);
      return;
    }

    if (editingId) {
      this.showNotice('Category update is not available via the API yet.', true);
      return;
    }

    this.settings.addCategory(this.categoryForm).subscribe((result) => {
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeModal();
      }
    });
  }

  deleteRow(row: Category): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    this.settings.deleteCategory(row.id).subscribe((result) => {
      this.showNotice(result.message, !result.success);
    });
  }

  private resetForm(): void {
    this.categoryForm = {
      code: '',
      name: '',
      description: '',
      status: 'active',
    };
  }

  private showNotice(message: string, isError = false): void {
    this.toastIsError.set(isError);
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }
}
